import React, { useState, useRef } from 'react';
import { Plus, Loader2, X, Edit2, Trash2, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { api } from '../../lib/api';

const MAX_IMAGES = 20;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const ImageLibrarySection = ({ images = [], onUpdate }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [error, setError] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [editCaption, setEditCaption] = useState('');
  const [isSavingCaption, setIsSavingCaption] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset input
    event.target.value = '';

    // Validações
    if (!file.type.startsWith('image/')) {
      setError('Selecione apenas arquivos de imagem (JPEG, PNG, WebP, GIF)');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError(`Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Máximo: 5MB`);
      return;
    }

    if (images.length >= MAX_IMAGES) {
      setError(`Limite de ${MAX_IMAGES} imagens atingido. Remova algumas para adicionar novas.`);
      return;
    }

    setError(null);
    setIsUploading(true);
    setUploadProgress('Enviando imagem...');

    try {
      const formData = new FormData();
      formData.append('file', file);

      setUploadProgress('Gerando descrição com IA...');

      const response = await api.post('/api/user/brand-identity/library/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.success && response.data?.data) {
        // Atualizar lista local
        if (onUpdate) {
          onUpdate([...images, response.data.data]);
        }
      }
    } catch (err) {
      console.error('Erro no upload:', err);
      setError(err.response?.data?.detail || 'Erro ao fazer upload da imagem');
    } finally {
      setIsUploading(false);
      setUploadProgress('');
    }
  };

  const handleEditClick = (image) => {
    setEditingImage(image);
    setEditCaption(image.caption || '');
    setError(null);
  };

  const handleSaveCaption = async () => {
    if (!editingImage) return;

    if (editCaption.length > 200) {
      setError('Descrição deve ter no máximo 200 caracteres');
      return;
    }

    setIsSavingCaption(true);
    setError(null);

    try {
      const response = await api.put(
        `/api/user/brand-identity/library/${editingImage.id}`,
        { caption: editCaption }
      );

      if (response.data?.success) {
        // Atualizar lista local
        if (onUpdate) {
          const updatedImages = images.map(img =>
            img.id === editingImage.id ? { ...img, caption: editCaption } : img
          );
          onUpdate(updatedImages);
        }
        setEditingImage(null);
        setEditCaption('');
      }
    } catch (err) {
      console.error('Erro ao salvar caption:', err);
      setError(err.response?.data?.detail || 'Erro ao salvar descrição');
    } finally {
      setIsSavingCaption(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Remover esta imagem da biblioteca?')) return;

    setDeletingId(imageId);
    setError(null);

    try {
      const response = await api.delete(`/api/user/brand-identity/library/${imageId}`);

      if (response.data?.success) {
        // Atualizar lista local
        if (onUpdate) {
          onUpdate(images.filter(img => img.id !== imageId));
        }
      }
    } catch (err) {
      console.error('Erro ao deletar:', err);
      setError(err.response?.data?.detail || 'Erro ao remover imagem');
    } finally {
      setDeletingId(null);
    }
  };

  const closeEditModal = () => {
    setEditingImage(null);
    setEditCaption('');
    setError(null);
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
          Biblioteca de Imagens
        </label>
        <span className="text-xs text-gray-400">
          {images.length}/{MAX_IMAGES}
        </span>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
          <button 
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Gallery Grid */}
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
        {/* Upload Button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading || images.length >= MAX_IMAGES}
          className={`aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-all ${
            isUploading || images.length >= MAX_IMAGES
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
              : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50 cursor-pointer'
          }`}
        >
          {isUploading ? (
            <>
              <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
              <span className="text-[10px] text-gray-500 text-center px-1">{uploadProgress}</span>
            </>
          ) : (
            <>
              <Plus className={`w-5 h-5 ${images.length >= MAX_IMAGES ? 'text-gray-300' : 'text-gray-400'}`} />
              <span className={`text-[10px] ${images.length >= MAX_IMAGES ? 'text-gray-300' : 'text-gray-500'}`}>
                Adicionar
              </span>
            </>
          )}
        </button>

        {/* Image Items */}
        {images.map((image) => (
          <div
            key={image.id}
            className="aspect-square rounded-xl overflow-hidden relative group bg-gray-100 border border-gray-200"
          >
            <img
              src={image.image_url}
              alt={image.caption || 'Imagem da biblioteca'}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><rect width="24" height="24"/></svg>';
              }}
            />
            
            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
              <button
                onClick={() => handleEditClick(image)}
                className="p-1.5 bg-white/90 rounded-lg hover:bg-white transition-colors"
                title="Editar descrição"
              >
                <Edit2 className="w-3.5 h-3.5 text-gray-700" />
              </button>
              <button
                onClick={() => handleDelete(image.id)}
                disabled={deletingId === image.id}
                className="p-1.5 bg-white/90 rounded-lg hover:bg-red-50 transition-colors"
                title="Remover"
              >
                {deletingId === image.id ? (
                  <Loader2 className="w-3.5 h-3.5 text-red-500 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                )}
              </button>
            </div>

            {/* Caption Preview (bottom) */}
            {image.caption && (
              <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-[9px] text-white line-clamp-2 leading-tight">
                  {image.caption}
                </p>
              </div>
            )}
          </div>
        ))}

        {/* Empty Placeholders */}
        {images.length === 0 && !isUploading && (
          <div className="col-span-3 sm:col-span-4 flex items-center justify-center py-6 text-gray-400">
            <div className="text-center">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs">Adicione imagens do seu negócio</p>
              <p className="text-[10px] mt-1 opacity-70">
                A IA vai descrever cada imagem automaticamente
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Info Text */}
      {images.length > 0 && (
        <p className="text-[10px] text-gray-400 mt-2">
          💡 Clique em uma imagem para editar sua descrição. A IA usa essas descrições para criar posts relevantes.
        </p>
      )}

      {/* Edit Modal */}
      {editingImage && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={closeEditModal}
        >
          <div 
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image Preview */}
            <div className="relative h-48 bg-gray-100">
              <img
                src={editingImage.image_url}
                alt="Preview"
                className="w-full h-full object-contain"
              />
              <button
                onClick={closeEditModal}
                className="absolute top-3 right-3 p-1.5 bg-white/90 rounded-full hover:bg-white transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Edit Form */}
            <div className="p-5">
              <h3 className="font-semibold text-gray-900 mb-1">Editar Descrição</h3>
              <p className="text-xs text-gray-500 mb-4">
                Esta descrição é usada pelo agente de IA para entender a imagem ao criar posts.
              </p>

              <textarea
                value={editCaption}
                onChange={(e) => setEditCaption(e.target.value.slice(0, 200))}
                placeholder="Descreva o que aparece nesta imagem..."
                rows={3}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-100 focus:border-amber-400 resize-none transition-all"
              />
              <div className="flex items-center justify-between mt-1.5 mb-4">
                <p className="text-[10px] text-gray-400">
                  Seja específico: produtos, pessoas, cenários, cores
                </p>
                <span className={`text-xs ${editCaption.length > 180 ? 'text-amber-600' : 'text-gray-400'}`}>
                  {editCaption.length}/200
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={closeEditModal}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveCaption}
                  disabled={isSavingCaption}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {isSavingCaption ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageLibrarySection;